import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import BackButton from '../../components/ui/BackButton';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import BudgetKPICard from './components/BudgetKPICard';
import BudgetCategoryChart from './components/BudgetCategoryChart';
import BudgetVarianceTable from './components/BudgetVarianceTable';
import BudgetFilters from './components/BudgetFilters';
import { supabase } from '../../lib/supabase';
import { siteService } from '../../services/siteService';
import { companyService } from '../../services/companyService';
import { pdfExportService } from '../../services/pdfExportService';
import { useLanguage } from '../../contexts/LanguageContext';

// Budget categories derived from task categories + site data
const BUDGET_CATEGORIES = [
  { key: 'Safety',        label: 'Safety',        defaultBudget: 50000 },
  { key: 'Quality',       label: 'Quality',       defaultBudget: 75000 },
  { key: 'Operations',    label: 'Operations',    defaultBudget: 120000 },
  { key: 'Documentation', label: 'Documentation', defaultBudget: 30000 },
  { key: 'Groundworks',   label: 'Groundworks',   defaultBudget: 85000 },
  { key: 'Structural',    label: 'Structural',    defaultBudget: 120000 },
  { key: 'Electrical',    label: 'Electrical',    defaultBudget: 65000 },
  { key: 'Plumbing',      label: 'Plumbing',      defaultBudget: 55000 },
  { key: 'Finishes',      label: 'Finishes',      defaultBudget: 75000 },
  { key: 'Preliminaries', label: 'Preliminaries', defaultBudget: 50000 },
];

const BudgetReport = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [filters, setFilters] = useState({ period: 'all', category: 'all' });
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState('all');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyName, setCompanyName] = useState('');
  const [exportLoading, setExportLoading] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    buildCategories();
  }, [selectedSiteId, filters]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadData = async () => {
    try {
      setLoading(true);
      const [sitesResult, companyResult] = await Promise.all([
        siteService?.getAllSites(),
        companyService?.getUserCompany(),
      ]);
      if (sitesResult?.data) setSites(sitesResult?.data || []);
      if (companyResult?.data) setCompanyName(companyResult?.data?.company_name || companyResult?.data?.name || '');
    } catch (err) {
      console.error('Error loading budget data:', err);
    } finally {
      setLoading(false);
    }
  };

  const buildCategories = async () => {
    try {
      // Fetch daily_tasks to derive actual spend per category
      let query = supabase
        ?.from('daily_tasks')
        ?.select('category, status, site_id, company_id');

      if (selectedSiteId !== 'all') {
        query = query?.eq('site_id', selectedSiteId);
      }

      const { data: tasks } = await query;
      const taskList = tasks || [];

      // Build category data: use task counts to derive a proportional spend estimate
      // Each completed task represents a unit of work cost
      const totalTasks = taskList?.length || 1;
      const completedTasks = taskList?.filter(t => t?.status === 'completed')?.length;

      const built = BUDGET_CATEGORIES?.map(cat => {
        const catTasks = taskList?.filter(t => t?.category === cat?.key);
        const catCompleted = catTasks?.filter(t => t?.status === 'completed')?.length;
        const catPending = catTasks?.filter(t => t?.status === 'pending' || t?.status === 'in_progress')?.length;

        // Derive actual spend proportionally from completed tasks
        const spendRatio = catTasks?.length > 0 ? catCompleted / catTasks?.length : 0;
        const actual = Math.round(cat?.defaultBudget * spendRatio * 0.85); // 85% of budget when fully done
        const committed = Math.round(cat?.defaultBudget * (catPending / Math.max(catTasks?.length, 1)) * 0.3);

        return {
          name: cat?.label,
          category: cat?.label,
          budget: cat?.defaultBudget,
          actual,
          committed,
          taskCount: catTasks?.length,
          completedCount: catCompleted,
        };
      })?.filter(c => {
        if (filters?.category !== 'all' && c?.name !== filters?.category) return false;
        return true;
      });

      setCategories(built);
    } catch (err) {
      console.error('Error building categories:', err);
      // Fallback to default budgets with zero actuals
      setCategories(BUDGET_CATEGORIES?.map(cat => ({
        name: cat?.label,
        category: cat?.label,
        budget: cat?.defaultBudget,
        actual: 0,
        committed: 0,
      })));
    }
  };

  const totals = useMemo(() => {
    const totalBudget    = categories?.reduce((s, c) => s + (c?.budget || 0), 0);
    const totalActual    = categories?.reduce((s, c) => s + (c?.actual || 0), 0);
    const totalCommitted = categories?.reduce((s, c) => s + (c?.committed || 0), 0);
    const remaining      = totalBudget - totalActual - totalCommitted;
    const spentPct       = totalBudget > 0 ? Math.round((totalActual / totalBudget) * 100) : 0;
    const overBudgetCount = categories?.filter(c => c?.actual > c?.budget)?.length;
    return { totalBudget, totalActual, totalCommitted, remaining, spentPct, overBudgetCount };
  }, [categories]);

  const handleExportPDF = async () => {
    try {
      setExportLoading(true);
      const activeSite = selectedSiteId !== 'all' ? sites?.find(s => s?.id === selectedSiteId) : null;
      const result = await pdfExportService?.generateBudgetReport({
        categories,
        totalBudget:    totals?.totalBudget,
        totalActual:    totals?.totalActual,
        totalCommitted: totals?.totalCommitted,
        remaining:      totals?.remaining,
        companyName,
        siteName: activeSite?.site_name || 'All Sites',
        period: filters?.period === 'all' ? 'All Time' : filters?.period,
      });
      if (result?.error) throw result.error;
      showToast(`PDF exported: ${result?.filename}`, 'success');
    } catch (err) {
      console.error('[BudgetReport] export PDF failed:', err);
      showToast('Failed to export PDF. Please try again.', 'error');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Budget Report | SiteProof</title>
      </Helmet>
      <Header />
      <main className="pt-[56px] min-h-screen bg-background">
        {/* Toast */}
        {toast && (
          <div className={`fixed top-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-sm font-medium ${
            toast?.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}>
            <Icon name={toast?.type === 'success' ? 'CheckCircle2' : 'AlertCircle'} size={16} />
            {toast?.message}
          </div>
        )}

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Page header */}
          <div className="mb-6">
            <BackButton fallback="/reports" />
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mt-3">
              <div>
                <h1 className="text-2xl font-bold text-foreground font-heading">{t('budgetReport')}</h1>
                <p className="text-sm text-muted-foreground mt-1">{t('budgetReportSubtitle')}</p>
              </div>
              <div className="flex items-center gap-3">
                {/* Site selector */}
                <select
                  value={selectedSiteId}
                  onChange={e => setSelectedSiteId(e?.target?.value)}
                  className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-accent"
                >
                  <option value="all">{t('allSites')}</option>
                  {sites?.map(s => <option key={s?.id} value={s?.id}>{s?.site_name}</option>)}
                </select>
                <BudgetFilters filters={filters} onChange={setFilters} />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleExportPDF}
                  disabled={exportLoading || loading}
                >
                  <Icon name={exportLoading ? 'Loader2' : 'Download'} size={15} className="mr-1.5" />
                  {exportLoading ? t('exporting') : t('exportPdf')}
                </Button>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-accent" />
            </div>
          ) : (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <BudgetKPICard
                  title={t('totalBudget')}
                  value={`£${(totals?.totalBudget / 1000)?.toFixed(0)}k`}
                  subtitle={t('projectAllocation')}
                  icon="PoundSterling"
                  variant="default"
                />
                <BudgetKPICard
                  title={t('actualSpend')}
                  value={`£${(totals?.totalActual / 1000)?.toFixed(0)}k`}
                  subtitle={`${totals?.spentPct}% ${t('ofBudget')}`}
                  icon="TrendingUp"
                  variant={totals?.spentPct > 90 ? 'danger' : totals?.spentPct > 70 ? 'warning' : 'success'}
                />
                <BudgetKPICard
                  title={t('remaining')}
                  value={`£${(totals?.remaining / 1000)?.toFixed(0)}k`}
                  subtitle={t('availableToSpend')}
                  icon="Wallet"
                  variant={totals?.remaining < 0 ? 'danger' : 'success'}
                />
                <BudgetKPICard
                  title={t('overBudget')}
                  value={`${totals?.overBudgetCount} ${t('items')}`}
                  subtitle={t('categoriesExceedingBudget')}
                  icon="AlertTriangle"
                  variant={totals?.overBudgetCount > 0 ? 'danger' : 'success'}
                />
              </div>

              {/* Budget progress bar */}
              <div className="bg-card rounded-xl border border-border shadow-sm p-6 mb-6">
                <div className="flex justify-between text-sm mb-3">
                  <span className="font-medium text-foreground">{t('overallBudgetUtilisation')}</span>
                  <span className="font-bold text-foreground">{totals?.spentPct}% {t('spent')}</span>
                </div>
                <div className="w-full bg-muted rounded-full h-5 overflow-hidden">
                  <div className="h-5 flex">
                    <div
                      className="bg-accent h-full transition-all"
                      style={{ width: `${totals?.spentPct}%` }}
                      title={`Spent: £${totals?.totalActual?.toLocaleString()}`}
                    />
                    <div
                      className="bg-amber-400 h-full transition-all"
                      style={{ width: `${Math.round((totals?.totalCommitted / Math.max(totals?.totalBudget, 1)) * 100)}%` }}
                      title={`Committed: £${totals?.totalCommitted?.toLocaleString()}`}
                    />
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-4 mt-3 text-xs">
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-accent" />
                    <span className="text-muted-foreground">{t('spent')} £{(totals?.totalActual / 1000)?.toFixed(0)}k ({totals?.spentPct}%)</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-amber-400" />
                    <span className="text-muted-foreground">{t('committed')} £{(totals?.totalCommitted / 1000)?.toFixed(0)}k</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-sm bg-muted border border-border" />
                    <span className="text-muted-foreground">{t('remaining')} £{(totals?.remaining / 1000)?.toFixed(0)}k</span>
                  </div>
                </div>
              </div>

              {/* Charts + Table */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                <BudgetCategoryChart categories={categories} />
                <div className="bg-card rounded-xl border border-border shadow-sm p-6">
                  <h3 className="font-semibold text-foreground mb-4">{t('categorySpendVsBudget')}</h3>
                  <div className="space-y-3">
                    {categories?.slice(0, 6)?.map((cat) => {
                      const spendPct = cat?.budget > 0 ? Math.round((cat?.actual / cat?.budget) * 100) : 0;
                      return (
                        <div key={cat?.name}>
                          <div className="flex items-center justify-between mb-1">
                            <span className="text-xs text-muted-foreground">{cat?.name}</span>
                            <span className="text-xs font-medium text-foreground">£{(cat?.actual / 1000)?.toFixed(0)}k / £{(cat?.budget / 1000)?.toFixed(0)}k</span>
                          </div>
                          <div className="w-full bg-muted rounded-full h-3 overflow-hidden">
                            <div
                              className={`h-3 rounded-full transition-all ${
                                spendPct > 100 ? 'bg-red-500' : spendPct > 80 ? 'bg-amber-400' : 'bg-accent'
                              }`}
                              style={{ width: `${Math.min(spendPct, 100)}%` }}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-4 pt-4 border-t border-border">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">{t('totalActualSpend')}</span>
                      <span className="font-bold text-foreground">£{(totals?.totalActual / 1000)?.toFixed(0)}k</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Variance Table */}
              <BudgetVarianceTable items={categories} />

              {/* Budget alerts */}
              {totals?.overBudgetCount > 0 && (
                <div className="mt-6 bg-red-50 border border-red-200 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <Icon name="AlertTriangle" size={20} className="text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-semibold text-red-800">{t('budgetAlert')}</p>
                      <p className="text-sm text-red-700 mt-0.5">
                        {totals?.overBudgetCount} {t('costCategory')} {totals?.overBudgetCount === 1 ? t('hasExceeded') : t('haveExceeded')} {t('allocatedBudget')}.
                        {t('reviewAndApprove')}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </main>
    </>
  );
};

export default BudgetReport;
